/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import React, {useEffect, useState} from 'react';
import Skeleton from "react-loading-skeleton";
import AppButton from "@/components/button/AppButton";
import AppTooltip from "@/components/tooltip/AppTooltip";
import AppPlansFeatures from "@/components/plans/AppPlansFeatures";
import {FaShoppingBag} from "react-icons/fa";
import {BsClock} from "react-icons/bs";
import {PiChatsCircle, PiEnvelopeOpenDuotone} from "react-icons/pi";
import {MdQrCode, MdRsvp, MdTableBar} from "react-icons/md";
import {IoLanguageOutline} from "react-icons/io5";
import {useGetPlansQuery} from '@/services/plansApi.ts';
import {apiMediaUrl, apiPlanUploadPath} from '@/utils/AppConfig';
import AppStatic from "@/utils/AppStatic";

const AppPlans = () => {

    const [initialLoading, setInitialLoading] = useState(false);
    const uploadPath = apiMediaUrl + apiPlanUploadPath;

    const {
        data: plans,
        isFetching,
        isLoading,
        isSuccess
    } = useGetPlansQuery();

    const loading = isFetching || isLoading;
    const successFetch = isSuccess;

    useEffect(() => {
        if (successFetch && !initialLoading) {
            setInitialLoading(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [successFetch]);

    const formatUpTimeLength = (upTimeLength: string | undefined): string => {
        if (!upTimeLength) {
            return '';
        }

        const [value, unit] = upTimeLength.toString().split('|');
        const numericValue = parseInt(value, 10);

        if (isNaN(numericValue)) {
            return '';
        }

        if (unit === 'd') {
            return `${numericValue}` + AppStatic.planDays.ro.translation;
        } else if (unit === 'm') {
            return `${numericValue}` + AppStatic.planMonths.ro.translation;
        }
        return '';
    };

    if (loading && !initialLoading) {
        return (
            <>
                <h3 className="text-4xl font-bold md:text-6xl mb-6">{AppStatic.plansHomeHeading.ro.translation}</h3>
                <Skeleton count={1} height={120}/>
                <Skeleton count={1} height={120}/>
                <Skeleton count={1} height={200}/>
            </>
        )
    }

    return (
        <div>
            <h3 className="text-4xl font-bold md:text-6xl mb-6">{AppStatic.plansAndPricesHeading.ro.translation}</h3>
            {plans && (
                <div className="app-plans">
                    <div>
                        {plans?.filter((plan: any) => plan.displayed && plan.price === 0)
                            .map((plan: any, index: number) => (
                                <div className="app-plan-shadow text-center p-8 rounded-3xl relative mb-3"
                                     key={index}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center">
                                        <div className="flex flex-wrap gap-4">
                                            <div>
												<img className="max-w-[48px]"
													   alt="Weddify by Diaphan"
													   src={uploadPath + plan.thumbnail}
													   loading={'lazy'}
												/>
                                            </div>
                                            <div className="text-left">
                                                <div className="text-sm font-thin text-mono-1000">
                                                    {plan.summary}
                                                </div>
                                                <div className="text-3xl">
                                                    {plan.name}
                                                </div>
                                            </div>
                                            <div className="w-full text-left text-mono-1000">
                                                <div className="font-thin text-left mt-2 text-sm leading-4 mb-2">
                                                    {plan.description}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="md:w-full lg:text-right text-mono-1000">
                                                {plan.price !== 0 ? (
                                                    <div
                                                        className="text-5xl font-bold text-left mt-1 relative mb-[1.2rem]">
                                                        <span>{plan.price} lei</span>
                                                        {plan.oldPrice > 0 && (
                                                            <span
                                                                className="font-thin relative line-through text-2xl ml-[15px] opacity-30">
                                                                {plan.oldPrice} lei
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="text-5xl font-bold text-left md:text-right mt-1 relative mb-2 uppercase">
                                                        <span>{AppStatic.planFree.ro.translation}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex justify-center md:justify-end">
                                                {plan.comingSoon ? (
                                                    <div className="w-full text-sm text-mono-200 italic">
                                                        *{AppStatic.planComingSoon.ro.translation}
                                                    </div>
                                                ) : (
                                                    <AppButton data={{
                                                        action: "link",
                                                        link: `/order/?plan=${plan._id}`,
                                                        extraClass: "bg-white! max-w-full! !md:max-w-[200px] text-black! border-black!",
                                                        name: AppStatic.planChoosePack.ro.translation,
                                                        icon: <FaShoppingBag className="f-2"/>
                                                    }}/>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-4 xl:grid-cols-3 text-black">
                        {plans?.filter((plan: any) => plan?.displayed && plan?.price > 0)
                            .map((plan: any, index: number) => (
                                <div className="app-plan-shadow text-center p-8 rounded-3xl relative" key={index}>
                                    <div className="flex flex-wrap gap-4">
                                        <div>
											<img className="max-w-[48px]"
												   alt="Weddify by Diaphan"
												   src={uploadPath + plan?.thumbnail}
												   loading="lazy"
											/>
                                        </div>
                                        <div className="text-left">
                                            <div className="text-sm font-thin text-mono-1000">{plan?.summary}</div>
                                            <div className="text-3xl">{plan?.name}</div>
                                        </div>
                                        <div className="w-full text-left text-mono-1000">
                                            <div className="font-thin text-left mt-2 text-sm leading-4 min-h-[58px]">
                                                {plan?.description}
                                            </div>
                                            {plan?.price !== 0 ? (
                                                <div className="text-5xl font-bold text-left mt-1 relative mb-[1.2rem]">
                                                    <span>{plan?.price} lei</span>
                                                    {plan?.oldPrice > 0 && (
                                                        <span
                                                            className="font-thin relative line-through text-2xl ml-[15px] text-[#EA1F25] opacity-70">
                                                                {plan?.oldPrice} lei
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div
                                                    className="text-5xl font-bold text-left mt-1 relative mb-[1.2rem] uppercase">
                                                    <span>{AppStatic.planFree.ro.translation}</span>
                                                </div>
                                            )}
                                            <div className="mb-20 grid grid-cols-2 gap-2">
                                                {plan?.upTimeLength && (
                                                    <div
                                                        className="p-4 bg-[#F9F5F5] text-lg rounded-lg flex items-center justify-center flex-col gap-2 relative">
                                                        <BsClock className="text-3xl"/>
                                                        <div className="text-center leading-none text-sm">
                                                            {formatUpTimeLength(plan?.upTimeLength)}
                                                        </div>
                                                    </div>
                                                )}
                                                {plan?.openAiOption && (
                                                    <div
                                                        className="p-4 bg-peach-base/10 text-lg rounded-lg flex justify-center  items-center flex-col gap-2 relative">
                                                        <PiChatsCircle className="text-3xl"/>
                                                        <div className="text-center leading-none text-sm">
                                                            {AppStatic.planIvyOption.ro.translation}
                                                        </div>
                                                        <div className="absolute right-1 top-1">
                                                            <AppTooltip data={
                                                                <>
                                                                    <div
                                                                        className="flex w-fit items-center gap-2 mb-5 px-4 py-2 ring-1 ring-inset ring-gray-300 rounded-lg">
                                                                        <PiChatsCircle
                                                                            className="text-3xl"/>
                                                                        <div className="text-center leading-none">
                                                                            {AppStatic.planIvyOption.ro.translation}
                                                                        </div>
                                                                    </div>
                                                                    <AppPlansFeatures features={[
                                                                        {text: AppStatic.planIvyUnlimitedMessages.ro.translation},
                                                                        {text: AppStatic.planIvyChatBox.ro.translation},
                                                                        {text: AppStatic.planIvyHistory.ro.translation},
                                                                        {text: AppStatic.planIvyLearn.ro.translation},
                                                                    ]}/>
                                                                </>
                                                            }/>
                                                        </div>
                                                    </div>
                                                )}
                                                {plan?.weddingOption && (
                                                    <>
                                                        <div
                                                            className="p-4 bg-gray-50 text-lg rounded-lg flex items-center justify-center flex-col gap-2 relative">
                                                            <PiEnvelopeOpenDuotone className="text-3xl"/>
                                                            <div className="text-center leading-none text-sm">
                                                                {AppStatic.planInvite.ro.translation}
                                                            </div>
                                                            <div className="absolute right-1 top-1">
                                                                <AppTooltip data={
                                                                    <>
                                                                        <div
                                                                            className="flex w-fit items-center gap-2 mb-5 px-4 py-2 ring-1 ring-inset ring-gray-300 rounded-lg">
                                                                            <PiEnvelopeOpenDuotone
                                                                                className="text-3xl"/>
                                                                            <div className="text-center leading-none">
                                                                                {AppStatic.planInvite.ro.translation}
                                                                            </div>
                                                                        </div>
                                                                        <AppPlansFeatures features={[
                                                                            {text: AppStatic.planInviteUnlimited.ro.translation},
                                                                            {text: AppStatic.planInviteEditUnlimited.ro.translation},
                                                                            {text: AppStatic.planInvitePersonalizable.ro.translation},
                                                                            {text: AppStatic.planCustomUrl.ro.translation},
                                                                            {text: AppStatic.planCoupleOption.ro.translation},
                                                                            {text: AppStatic.planWeddingEventOption.ro.translation},
                                                                            {text: AppStatic.planWeddingEventOptions.ro.translation},
                                                                            {text: AppStatic.planWeddingEventLinks.ro.translation},
                                                                            {text: AppStatic.planGalleryImagesNo.ro.translation},
                                                                            {text: AppStatic.planRsvpOption.ro.translation},
                                                                            {text: AppStatic.planInviteSong.ro.translation},
                                                                            {text: AppStatic.planInviteQR.ro.translation},
                                                                            {text: AppStatic.planInviteOnOff.ro.translation},
                                                                            {text: AppStatic.planWeddingOnlySimplified.ro.translation},
                                                                            {text: AppStatic.planWeddingComplete.ro.translation},
                                                                        ]}/>
                                                                    </>
                                                                }/>
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="p-4 bg-gray-50 text-lg rounded-lg flex items-center justify-center flex-col gap-2 relative">
                                                            <MdRsvp className="text-3xl"/>
                                                            <div className="text-center leading-none text-sm">
                                                                {AppStatic.planRSVP.ro.translation}
                                                            </div>
                                                            <div className="absolute right-1 top-1">
                                                                <AppTooltip data={
                                                                    <>
                                                                        <div
                                                                            className="flex w-fit items-center gap-2 mb-5 px-4 py-2 ring-1 ring-inset ring-gray-300 rounded-lg">
                                                                            <MdRsvp className="text-3xl"/>
                                                                            <div className="text-center leading-none">
                                                                                {AppStatic.planRSVP.ro.translation}
                                                                            </div>
                                                                        </div>
                                                                        <AppPlansFeatures features={[
                                                                            {text: AppStatic.planRSVPUnlimited.ro.translation},
                                                                            {text: AppStatic.planRSVPEditableFields.ro.translation},
                                                                            {text: AppStatic.planRSVPEmail.ro.translation},
                                                                            {text: AppStatic.planRSVPNotifications.ro.translation},
                                                                            {text: AppStatic.planRSVPDashboard.ro.translation},
                                                                            {text: AppStatic.planRsvpExport.ro.translation},
                                                                            {text: AppStatic.planRSVPStatistics.ro.translation},
                                                                            {text: AppStatic.planRSVPFilteredSearch.ro.translation},
                                                                            {text: AppStatic.planRSVPEditConfirmations.ro.translation},
                                                                            {text: AppStatic.planRSVPAddConfirmations.ro.translation},
                                                                            {text: AppStatic.planRSVPOnOff.ro.translation},
                                                                        ]}/>
                                                                    </>
                                                                }/>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                                {plan?.placementOption && (
                                                    <div
                                                        className="p-4 bg-[#AFB6B6]/10 text-lg rounded-lg flex justify-center  items-center flex-col gap-2 relative">
                                                        <MdTableBar className="text-3xl"/>
                                                        <div className="text-center leading-none text-sm">
                                                            {AppStatic.planPlacement.ro.translation}
                                                        </div>
                                                        <div className="absolute right-1 top-1">
                                                            <AppTooltip data={
                                                                <>
                                                                    <div
                                                                        className="flex w-fit items-center gap-2 mb-5 px-4 py-2 ring-1 ring-inset ring-gray-300 rounded-lg">
                                                                        <MdTableBar className="text-3xl"/>
                                                                        <div className="text-center leading-none">
                                                                            {AppStatic.planPlacement.ro.translation}
                                                                        </div>
                                                                    </div>
                                                                    <AppPlansFeatures features={[
                                                                        {text: AppStatic.planPlacementUnlimitedTables.ro.translation},
                                                                        {text: AppStatic.planPlacementUnlimitedGuests.ro.translation},
                                                                        {text: AppStatic.planPlacementGroupsRelations.ro.translation},
                                                                        {text: AppStatic.planPlacementDesignTools.ro.translation},
                                                                        {text: AppStatic.planPlacementImportGuests.ro.translation},
                                                                        {text: AppStatic.planPlacementDragAndDrop.ro.translation},
                                                                        {text: AppStatic.planPlacementAI.ro.translation},
                                                                        {text: AppStatic.planPlacementOpisExport.ro.translation},
                                                                        {text: AppStatic.planPlacementPlanExport.ro.translation},
                                                                    ]}/>
                                                                </>
                                                            }/>
                                                        </div>
                                                    </div>
                                                )}
                                                {plan?.wedSnapOption && (
                                                    <div
                                                        className="p-4 bg-[#EBE6DE]/30 text-lg rounded-lg flex justify-center items-center flex-col gap-2 relative">
                                                        <MdQrCode className="text-3xl"/>
                                                        <div className="text-center leading-none text-sm">
                                                            {AppStatic.planWedSnap.ro.translation}
                                                        </div>
                                                        <div className="absolute right-1 top-1">
                                                            <AppTooltip data={
                                                                <>
                                                                    <div
                                                                        className="flex w-fit items-center gap-2 mb-5 px-4 py-2 ring-1 ring-inset ring-gray-300 rounded-lg">
                                                                        <MdQrCode className="text-3xl"/>
                                                                        <div className="text-center leading-none">
                                                                            {AppStatic.planWedSnap.ro.translation}
                                                                        </div>
                                                                    </div>
                                                                    <AppPlansFeatures features={[
                                                                        {text: AppStatic.planWedSnapUnlimited.ro.translation},
                                                                        {text: AppStatic.planWedSnapShare.ro.translation},
                                                                        {text: AppStatic.planWedSnapNoAppRequired.ro.translation},
                                                                        {text: AppStatic.planWedSnapSocialMediaLayout.ro.translation},
                                                                        {text: AppStatic.planWedSnapPersonalize.ro.translation},
                                                                        {text: AppStatic.planWedSnapEventDate.ro.translation},
                                                                        {text: AppStatic.planWedSnapMessage.ro.translation},
                                                                        {text: AppStatic.planWedSnapDownload.ro.translation},
                                                                        {text: AppStatic.planWedSnapReport.ro.translation},
                                                                    ]}/>
                                                                </>
                                                            }/>
                                                        </div>
                                                    </div>
                                                )}
                                                {plan?.translatableOption && (
                                                    <div
                                                        className="p-4 bg-[#EBE6DE]/30 text-lg rounded-lg flex justify-center items-center flex-col gap-2 relative">
                                                        <IoLanguageOutline className="text-3xl"/>
                                                        <div className="text-center leading-none text-sm">
                                                            {AppStatic.planTranslatable.ro.translation}
                                                        </div>
                                                        <div className="absolute right-1 top-1">
                                                            <AppTooltip data={
                                                                <>
                                                                    <div
                                                                        className="flex w-fit items-center gap-2 mb-5 px-4 py-2 ring-1 ring-inset ring-gray-300 rounded-lg">
                                                                        <IoLanguageOutline className="text-3xl"/>
                                                                        <div className="text-center leading-none">
                                                                            {AppStatic.planTranslatable.ro.translation}
                                                                        </div>
                                                                    </div>
                                                                    <AppPlansFeatures features={[
                                                                        {text: AppStatic.planTranslatableLanguages.ro.translation},
                                                                        {text: AppStatic.planTranslatableAutomaticTranslation.ro.translation},
                                                                        {text: AppStatic.planTranslatableLinks.ro.translation},
                                                                        {text: AppStatic.planTranslatableSwitch.ro.translation},
                                                                        {text: AppStatic.planTranslatableEdit.ro.translation},
                                                                        {text: AppStatic.planTranslatableAvailableLanguages.ro.translation}
                                                                    ]}/>
                                                                </>
                                                            }/>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className="absolute w-full px-8 bottom-6 left-1/2 transform -translate-x-1/2 m-auto">
                                            {plan?.comingSoon ? (
                                                <div className="w-full text-sm text-mono-200 italic">
                                                    *{AppStatic.planComingSoon.ro.translation}
                                                </div>
                                            ) : (
                                                <AppButton data={{
                                                    action: "link",
                                                    link: `/order/?plan=${plan._id}`,
                                                    extraClass: "w-full! max-w-full! bg-white! text-black! border-black!",
                                                    name: AppStatic.planChoosePack.ro.translation,
                                                    icon: <FaShoppingBag className="f-2"/>
                                                }}/>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>
            )}
        </div>
    );
}

export default AppPlans;
